import { useEffect, useState } from 'react'
import { useForm } from '@inertiajs/react'
import BrandMark from './BrandMark'
import type { LeadFormLabels } from '@/pages/landing/types'

export default function LeadForm({ labels, variant = 'default', kegCountHint, source }: {
  labels: LeadFormLabels
  variant?: 'default' | 'compact'
  // Mirrors the ROI calculator's slider so the visitor never types their fleet size twice.
  // The distribution of these values is what will settle where the pricing blocks sit.
  kegCountHint?: number
  source: string
}) {
  const { data, setData, post, processing, errors, wasSuccessful } = useForm({
    email: '',
    brewery_name: '',
    keg_count: '',
    source,
  })
  const [countEdited, setCountEdited] = useState(false)

  // Follow the slider until the visitor types their own figure, then stop overwriting it.
  useEffect(() => {
    if (countEdited || kegCountHint == null) return

    setData('keg_count', String(kegCountHint))
  }, [kegCountHint, countEdited]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setData('source', source)
  }, [source]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/leads', { preserveScroll: true })
  }

  if (wasSuccessful) {
    const isCompact = variant === 'compact'
    return (
      <div className={`rounded-xl p-6 text-center border-2 ${isCompact ? 'bg-paper-2 border-ink' : 'bg-[#EAF4E3] border-leaf'}`}>
        <BrandMark className="w-10 h-12 mx-auto mb-2" />
        <p className={`font-body font-extrabold text-xl mb-1 ${isCompact ? 'text-ink' : 'text-leaf-deep'}`}>{labels.success_title}</p>
        <p className="text-sm text-ink-soft">{labels.success_body}</p>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={data.email}
          onChange={(e) => setData('email', e.target.value)}
          placeholder={labels.compact_ph}
          required
          className="flex-1 rounded-[12px] border-2 border-ink bg-paper px-5 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-berry/40"
        />
        <button
          type="submit"
          disabled={processing}
          className="shrink-0 font-bold text-paper bg-flame hover:bg-flame-deep border-2 border-ink px-6 py-3 rounded-[12px] text-sm shadow-hard transition-all disabled:opacity-50"
        >
          {processing ? labels.submitting : labels.compact_submit}
        </button>
        {errors.email && <p className="text-xs text-red-400 sm:col-span-2">{errors.email}</p>}
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-ink mb-1">{labels.email_label}</label>
        <input
          type="email"
          value={data.email}
          onChange={(e) => setData('email', e.target.value)}
          placeholder={labels.email_ph}
          required
          className="w-full rounded-[12px] border-2 border-ink bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-berry/40"
        />
        {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-ink mb-1">{labels.brewery_label} <span className="text-ink-faint">{labels.optional}</span></label>
        <input
          type="text"
          value={data.brewery_name}
          onChange={(e) => setData('brewery_name', e.target.value)}
          placeholder={labels.brewery_ph}
          className="w-full rounded-[12px] border-2 border-ink bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-berry/40"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-ink mb-1">{labels.count_label} <span className="text-ink-faint">{labels.optional}</span></label>
        <input
          type="number"
          value={data.keg_count}
          onChange={(e) => { setCountEdited(true); setData('keg_count', e.target.value) }}
          placeholder={labels.count_ph}
          min={1}
          className="w-full rounded-[12px] border-2 border-ink bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-berry/40"
        />
      </div>

      <button
        type="submit"
        disabled={processing}
        className="w-full font-bold text-paper bg-flame hover:bg-flame-deep border-2 border-ink px-8 py-4 rounded-[14px] text-lg shadow-hard transition-all disabled:opacity-50"
      >
        {processing ? labels.submitting : labels.submit}
      </button>

      <p className="text-xs text-ink-faint text-center">{labels.disclaimer}</p>
    </form>
  )
}
